// SPDX-License-Identifier: BUSL-1.1

/**
 * RunnerClassTable — displays tenant's fireactions runner pools.
 *
 * Columns: Name, Labels, Replicas, CPU/RAM, OS, Version Compliance
 *
 * Fetches from GET /api/tenant/runner-classes
 */

import type { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { DataTable } from '@/components/data-table';
import type { RunnerClass } from '@/types/runner-class';

/* ── API types ─────────────────────────────────── */

interface RunnerClassesResponse {
  runnerClasses: RunnerClass[];
  total: number;
}

/* ── Semver helpers ────────────────────────────── */

interface SemVer {
  major: number;
  minor: number;
  patch: number;
}

function parseSemver(version: string): SemVer | null {
  const match = version.match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (!match) return null;
  return { major: Number(match[1]), minor: Number(match[2]), patch: Number(match[3]) };
}

/**
 * Compare agent version against fireactions version.
 *
 * - `compatible`: versions match or only patch differs
 * - `mismatch`: minor or major version differs (agent may be ahead/behind)
 * - `unknown`: one or both versions missing
 */
function compareVersions(
  agentVersion: string | undefined,
  fireactionsVersion: string | undefined,
): 'compatible' | 'mismatch' | 'unknown' {
  if (!agentVersion || !fireactionsVersion) return 'unknown';
  const agent = parseSemver(agentVersion);
  const server = parseSemver(fireactionsVersion);
  if (!agent || !server) return 'unknown';

  if (agent.major !== server.major) return 'mismatch';
  if (agent.minor !== server.minor) return 'mismatch';
  return 'compatible';
}

/* ── Version compliance badge ──────────────────── */

interface VersionComplianceBadgeProps {
  agentVersion?: string;
  fireactionsVersion?: string;
}

function VersionComplianceBadge({ agentVersion, fireactionsVersion }: VersionComplianceBadgeProps) {
  const status = compareVersions(agentVersion, fireactionsVersion);

  if (status === 'unknown') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-neutral-800 px-2 py-0.5 text-xs font-semibold text-neutral-400"
        title="Version data unavailable. Agent or fireactions version not reported."
      >
        <span className="size-1.5 rounded-full bg-neutral-500" />
        UNKNOWN
      </span>
    );
  }

  if (status === 'compatible') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-green-950/40 px-2 py-0.5 text-xs font-semibold text-green-400"
        title={`Versions compatible — agent ${agentVersion}, fireactions ${fireactionsVersion}`}
      >
        <span className="size-1.5 rounded-full bg-green-400" />
        COMPATIBLE
      </span>
    );
  }

  // mismatch
  const agent = parseSemver(agentVersion!);
  const server = parseSemver(fireactionsVersion!);
  const isMajor = agent && server && agent.major !== server.major;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
        isMajor
          ? 'bg-red-950/40 text-red-400'
          : 'bg-amber-950/40 text-amber-400'
      }`}
      title={`Version ${isMajor ? 'major' : 'minor'} mismatch — agent ${agentVersion}, fireactions ${fireactionsVersion}. Upgrade recommended.`}
    >
      <span className={`size-1.5 rounded-full ${isMajor ? 'bg-red-400' : 'bg-amber-400'}`} />
      {isMajor ? 'MISMATCH' : 'OUTDATED'}
    </span>
  );
}

/* ── OS badge ──────────────────────────────────── */

function OsBadge({ os }: { os: RunnerClass['capabilities']['os'] }) {
  const colors: Record<RunnerClass['capabilities']['os'], string> = {
    linux: 'bg-neutral-700 text-neutral-300',
    windows: 'bg-blue-900/30 text-blue-400',
    macos: 'bg-neutral-600 text-neutral-200',
  };
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs ${colors[os]}`}>
      {os}
    </span>
  );
}

/* ── Columns ───────────────────────────────────── */

const columns: ColumnDef<RunnerClass>[] = [
  {
    accessorKey: 'name',
    header: 'Pool Name',
    cell: ({ row }) => (
      <span className="font-mono text-sm text-neutral-200">{row.original.name}</span>
    ),
  },
  {
    accessorKey: 'labels',
    header: 'Labels',
    cell: ({ row }) => {
      const labels = row.original.labels;
      if (!labels.length) {
        return <span className="text-neutral-600">—</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {labels.map((label) => (
            <span
              key={label}
              className="inline-flex rounded bg-neutral-800 px-1.5 py-0.5 font-mono text-xs text-neutral-400"
            >
              {label}
            </span>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: 'replicas',
    header: 'Replicas',
    cell: ({ row }) => (
      <span className="text-neutral-200">{row.original.replicas}</span>
    ),
  },
  {
    id: 'resources',
    header: 'CPU / RAM',
    cell: ({ row }) => {
      const { cpu, ramGb } = row.original.capabilities;
      return (
        <span className="text-neutral-400">
          {cpu} vCPU · {ramGb} GB
        </span>
      );
    },
  },
  {
    accessorKey: 'capabilities',
    header: 'OS',
    cell: ({ row }) => <OsBadge os={row.original.capabilities.os} />,
  },
  {
    id: 'health',
    header: 'Version',
    cell: ({ row }) => (
      <VersionComplianceBadge
        agentVersion={row.original.agentVersion}
        fireactionsVersion={row.original.fireactionsVersion}
      />
    ),
  },
];

/* ── Table component ───────────────────────────── */

export function RunnerClassTable() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['tenant-runner-classes'],
    queryFn: () => apiFetch<RunnerClassesResponse>('/tenant/runner-classes'),
  });

  if (isLoading) {
    return (
      <div className="rounded border border-neutral-800 bg-neutral-900 p-4 text-sm text-neutral-400">
        Loading runner classes…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded border border-red-900 bg-red-900/20 p-4 text-sm text-red-300">
        Failed to load runner classes: {String(error)}
      </div>
    );
  }

  const runnerClasses = data?.runnerClasses ?? [];

  if (runnerClasses.length === 0) {
    return (
      <div className="rounded border border-neutral-800 bg-neutral-900 p-4 text-sm text-neutral-500">
        No runner pools configured. Add a fireactions pool to your VM500 config to get
        started.
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={runnerClasses}
      emptyMessage="No runner pools found for this tenant."
      pageSize={10}
      pageSizeOptions={[10, 25, 50]}
    />
  );
}